import React, { FC, ReactNode } from 'react';
import { classnames } from 'helpers/utils/classnames';

type ConfiguratorStepProps = {
  /**
   * Position in the step sequence, 1-based. Numbers follow render order, so they are assigned by
   * the configurator rather than baked into each step. Omitted for steps that are optional
   * add-ons (wirebrush) rather than a required pick.
   */
  stepNumber?: number;
  label: string;
  /** Currently selected value, shown inline after the label. Omitted when nothing is selected yet. */
  selectedValue?: string | null;
  /**
   * A short action that belongs to the whole step, set on the header's right ("Get a sample" on
   * Finish). It was buried under the last swatch group before (Joel, 4 Oct 2026); in the header it is
   * seen before the shopper starts comparing options and stays put however long the list is.
   */
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
};

/**
 * Shared chrome for one configurator step: a rule above, then the numbered label with the current
 * selection set immediately after it. The selection carries the weight because it is the part a
 * shopper scans for — reading down the column gives them their whole configuration, with the
 * step names receding into labels.
 */
const ConfiguratorStep: FC<ConfiguratorStepProps> = ({
  stepNumber,
  label,
  selectedValue,
  aside,
  className,
  children,
}) => (
  <div className={classnames('mt-20 border-t border-[#e8e8e8] pt-12', className)}>
    {/*
      The colon belongs to the label, so it stays unweighted — only the value is bold.
      text-tertiary, not text-secondary: the latter maps to blue-dark in this token set.
    */}
    <div className="mb-8 flex flex-wrap items-baseline justify-between gap-x-16 gap-y-4">
      <h2 className="text-16 text-[#212121]">
        <span className="text-tertiary">{`${stepNumber ? `${stepNumber}. ${label}` : label}${selectedValue ? ': ' : ''}`}</span>
        {selectedValue && <span className="font-semibold text-[#212121]">{selectedValue}</span>}
      </h2>
      {aside && <div className="shrink-0 text-14">{aside}</div>}
    </div>
    {children}
  </div>
);

export default ConfiguratorStep;
